import React, { useEffect, useRef } from 'react';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

/**
 * Shows the candidate the document they actually uploaded.
 *
 * Without this, a candidate asked to confirm a document is the right one has to remember
 * which file they picked — and on a phone, where the file is called IMG_20260916_164233
 * and sits among forty others, they cannot. Accepting a document is a decision, and this
 * is the evidence for it.
 *
 * The source is either a blob URL for a file picked in this tab or a short-lived
 * presigned URL for one already on S3 (a refresh loses the File object, not the upload).
 * The caller owns both; this component only renders what it is handed.
 */
export default function DocumentPreviewModal({ open, onClose, source, fileName, loading, error }) {
  const closeRef = useRef(null);
  const isPdf =
    String(source?.mimeType || '').includes('pdf') ||
    /\.pdf(\?|$)/i.test(String(fileName || '')) ||
    /\.pdf(\?|$)/i.test(String(source?.url || ''));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // The page behind a full-screen preview must not scroll under it on touch.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${fileName || 'your document'}`}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-full flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
          <p className="text-sm font-bold text-gray-900 truncate">{fileName || 'Your document'}</p>
          <div className="flex items-center gap-1 shrink-0">
            {source?.url && (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition"
                aria-label="Open in a new tab"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              </a>
            )}
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition cursor-pointer"
              aria-label="Close preview"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto bg-slate-50 flex items-center justify-center p-3">
          {loading ? (
            <p className="text-sm text-gray-500 py-12">Loading your document…</p>
          ) : error ? (
            <p className="text-sm text-red-600 py-12 px-4 text-center">{error}</p>
          ) : !source?.url ? (
            <p className="text-sm text-gray-500 py-12">Nothing to preview.</p>
          ) : isPdf ? (
            <object
              data={source.url}
              type="application/pdf"
              className="w-full h-[70vh] rounded-lg"
              aria-label={fileName || 'Document preview'}
            >
              {/* iOS Safari will not render a PDF in an object, so give it a way out. */}
              <p className="text-sm text-gray-600 p-6 text-center">
                Your browser cannot show this PDF inline.{' '}
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 font-semibold underline"
                >
                  Open it in a new tab
                </a>
                .
              </p>
            </object>
          ) : (
            <img
              src={source.url}
              alt={fileName || 'Your uploaded document'}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          )}
        </div>
      </div>
    </div>
  );
}
